
import { motion } from "framer-motion";
import { LayoutGrid } from "lucide-react";
import { LocationDistribution } from "@/utils/dashboardUtils";

interface StorageLocationOverviewProps {
  locationDistribution: LocationDistribution[];
  isLoading: boolean;
  totalInventory: number;
}

export default function StorageLocationOverview({ 
  locationDistribution, 
  isLoading, 
  totalInventory 
}: StorageLocationOverviewProps) {
  if (isLoading || totalInventory === 0) {
    return null; // Loading or empty state is handled in parent component
  }

  // Prepare locations for display
  const actualLocationDistribution = locationDistribution.map(location => ({
    name: location.name,
    value: location.value,
    items: `${location.value} items`,
    percentage: location.percentage || 0
  }));

  return (
    <motion.div 
      className="bg-white rounded-xl p-6 shadow-sm mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <h3 className="text-xl font-semibold mb-4 flex items-center">
        <LayoutGrid className="h-5 w-5 mr-2 text-expitrack-primary" /> 
        Storage Location Overview
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {actualLocationDistribution.slice(0, 4).map((location, index) => (
          <div 
            key={index} 
            className="border rounded-lg p-4 flex flex-col items-center text-center hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 rounded-full bg-expitrack-primary/10 flex items-center justify-center mb-3">
              <LayoutGrid className={`h-6 w-6 ${
                index === 0 ? "text-expitrack-primary" : 
                index === 1 ? "text-blue-500" : 
                index === 2 ? "text-cyan-500" : 
                "text-violet-500"
              }`} />
            </div>
            <h4 className="font-medium mb-1">{location.name}</h4>
            <p className="text-2xl font-bold">{location.value}</p>
            <p className="text-xs text-gray-500">items</p>
          </div>
        ))}
      </div>
      <div className="text-center mt-4 text-sm text-gray-500">
        <p>Distribution based on your inventory data</p>
      </div>
    </motion.div>
  );
}
